<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { DgrDialog, DgrIcon } from '@stockmarkteam/donguri-ui';
import AutoResizeTextarea from '@/components/survey/input/auto-resize-textarea.vue';
import { PromptTemplate, PromptTemplateItem } from '@/types';
import { SURVEY_QUESTION_MAX_LENGTH } from '@/utils/survey/constants';

const MAX_DISPLAYABLE_DESCRIPTION_LENGTH = 84;

/**
 * Props
 */

interface Props {
  template: PromptTemplate;
  isFavorite: boolean;
  isNew: boolean;
}

const props = defineProps<Props>();

const { template } = toRefs(props);

/**
 * Emits
 */

const emit = defineEmits<{
  'apply-prompt-template': [
    prompt: string,
    template: PromptTemplate,
    isFavorite: boolean,
  ];
  'toggle-favorite': [template: PromptTemplate];
}>();

/**
 * Local States
 */

const isOpen = ref(false);

const formValues = ref<Record<string, string>>({});

const hasEmptyRequiredItems = computed(() =>
  template.value.surveyPromptTemplateItems.some(
    item => item.required && !formValues.value[item.itemName]?.trim(),
  ),
);

/**
 * Handlers
 */

const openModal = () => (isOpen.value = true);

const applyPromptTemplate = () => {
  if (hasEmptyRequiredItems.value) return;

  const outputPrompt = Object.entries(formValues.value).reduce(
    (acc, [key, value]) => acc.replace(`<%${key}%>`, value),
    template.value.template,
  );
  emit('apply-prompt-template', outputPrompt, template.value, props.isFavorite);
  isOpen.value = false;
};

const setFormValues = () => {
  formValues.value = template.value.surveyPromptTemplateItems.reduce(
    (acc: Record<string, string>, item: PromptTemplateItem) => {
      acc[item.itemName] = item.defaultValue || '';
      return acc;
    },
    {},
  );
};

watch(template, setFormValues, { immediate: true });
</script>

<template>
  <div class="survey-prompt-template-card" @click="openModal">
    <span class="title">
      <span class="c-text-regular--xm">
        {{ template.title }}
      </span>
      <DgrIcon
        :name="isFavorite ? 'star-fill' : 'star'"
        :keep-fill="false"
        @click.stop="emit('toggle-favorite', template)"
        v-tooltip.top="isFavorite ? 'お気に入り解除' : 'お気に入り'"
        size="small"
        class="favorite-button"
        :class="{
          active: isFavorite,
        }"
      />
    </span>
    <div
      class="description c-text-regular--m"
      v-if="template.description"
      v-tooltip.bottom="
        template.description.length > MAX_DISPLAYABLE_DESCRIPTION_LENGTH
          ? {
              content: template.description,
              delay: { show: 300 },
              autoHide: false,
            }
          : undefined
      "
    >
      {{ template.description }}
    </div>
    <span class="new c-text-regular--s" v-if="isNew">新着</span>
    <DgrDialog
      v-model:open="isOpen"
      :title="template.title"
      :modal="false"
      :has-footer="true"
      submit-label="プロンプトを作成"
      @submit="applyPromptTemplate"
    >
      <template #description>
        <div class="modal-body">
          <div
            class="modal-description c-text-regular--m"
            v-if="template.description"
          >
            {{ template.description }}
          </div>
          <div class="form-items c-text-regular--m">
            <template
              v-for="(item, idx) in template.surveyPromptTemplateItems"
              :key="`form-${idx}-item-${item.itemName}`"
            >
              <div class="form-item">
                <label :for="item.itemName" class="c-title-bold--m">
                  {{ item.label }}
                  <template v-if="item.required">(必須)</template>
                </label>
                <input
                  v-if="item.elementType === 'input'"
                  v-model="formValues[item.itemName]"
                  type="text"
                  :id="item.itemName"
                  :placeholder="item.placeholder || ''"
                  :maxlength="SURVEY_QUESTION_MAX_LENGTH"
                />
                <AutoResizeTextarea
                  v-else-if="item.elementType === 'textarea'"
                  v-model="formValues[item.itemName]"
                  :max-height="300"
                  :id="item.itemName"
                  :placeholder="item.placeholder || ''"
                  :rows="3"
                  :maxlength="SURVEY_QUESTION_MAX_LENGTH"
                />
              </div>
            </template>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="cancel-button">
          <button
            ref="cancelButtonRef"
            type="button"
            class="c-btn c-btn--auto c-btnOutline"
            @click="isOpen = false"
          >
            キャンセル
          </button>
        </div>
        <div class="submit-button">
          <button
            type="button"
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            :class="{ disabled: hasEmptyRequiredItems }"
            :disabled="hasEmptyRequiredItems"
            @click="applyPromptTemplate"
          >
            プロンプトを作成
          </button>
        </div>
      </template>
    </DgrDialog>
  </div>
</template>

<style lang="scss" scoped>
.survey-prompt-template-card {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 94px;
  padding: 16px;
  background-color: $color-white;
  border-radius: 4px;
  border: solid 1px $color-gray400;
  cursor: pointer;

  &:hover {
    border: solid 1px $color-gray1000;
  }
}
.title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .favorite-button {
    flex-shrink: 0;
    align-self: flex-start;
    padding: 4px;
    &.active {
      color: $color-or7;
    }
  }
}
.description {
  color: $color-gray800;
  margin-top: 4px;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  word-break: break-all;
}
.new {
  display: inline-flex;
  width: fit-content;
  margin-top: auto;
  padding: 0 4px;
  background-color: $color-green100;
  color: $color-green600;
  border-radius: 2px;
}

/* Modal */
.modal-body {
  min-width: 688px;
  max-width: 688px;
}

.modal-description {
  padding-bottom: 16px;
}

.form-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  input {
    width: 100%;
    border: solid 1px $color-gray400;
    padding: 12px;
    box-sizing: border-box;
  }

  textarea {
    width: 100%;
    border: solid 1px $color-gray400;
    max-height: 300px;
    padding: 12px;
    box-sizing: border-box;
  }

  ::placeholder {
    color: $color-gray600;
  }
}
/* Modal */
</style>
